import { api } from '../../api/client';

export function approvePurchase(
  purchaseId: number,
  _sellerId: number,
  shipping?: {
    offerId: string;
    maxShippingCost: number;
    allowLate?: boolean;
    // Identidad estable de la oferta elegida — el offerId de Amazon rota por sesión
    sellerName?: string;
    price?: number;
  }
) {
  // Backend only needs purchaseId from URL, no seller_id in body.
  // Con `shipping` se compra la oferta elegida (EXPEDITED_PAID + tope de envío).
  return api.post(`/amazon/purchases/${purchaseId}/approve`, shipping ?? {});
}

/** Compra retenida por suba de precio (safeguard_hold / PRICE_JUMP_AT_EXECUTE):
 *  el usuario acepta comprar HASTA `acceptPriceUpTo` por unidad (el precio que vio
 *  en la leyenda). Si Amazon subió de nuevo por encima, el backend vuelve a frenar. */
export function approvePurchaseAcceptingPrice(purchaseId: number, acceptPriceUpTo: number) {
  return api.post(`/amazon/purchases/${purchaseId}/approve`, { acceptPriceUpTo });
}

export interface ShippingOption {
  offerId: string;
  sellerName: string;
  price: number;
  shippingCost: number;
  deliveryDays: number;
  deliveryMax: string | null;
  onTime: boolean;
  estProfit: number;
  meetsFloor: boolean;
}

export async function fetchShippingOptions(purchaseId: number): Promise<{
  maxDeliveryDays: number | null;
  minProfit: number | null;
  profitFloor: number | null;
  options: ShippingOption[];
}> {
  const res = await api.get<{
    maxDeliveryDays: number | null;
    minProfit: number | null;
    profitFloor: number | null;
    options: ShippingOption[];
  }>(`/amazon/purchases/${purchaseId}/shipping-options`);
  if (!res.success || !res.data)
    throw new Error((res as { error?: string }).error || 'No se pudieron cargar las opciones');
  return res.data;
}

/** needs_review → ordered: la compra YA está hecha en Amazon; solo se acepta tal como quedó. */
export function confirmPurchase(purchaseId: number, _sellerId: number) {
  return api.post(`/amazon/purchases/${purchaseId}/confirm`, {});
}

export function retryPurchase(purchaseId: number, _sellerId: number) {
  return api.post(`/amazon/purchases/${purchaseId}/retry`, {});
}

export function cancelPurchase(purchaseId: number, _sellerId: number) {
  return api.post(`/amazon/purchases/${purchaseId}/cancel`, { reason: 'Cancelled from order detail' });
}

export function cancelMlOrder(mlOrderId: number, sellerId: number) {
  return api.post(`/mercadolibre/orders/${mlOrderId}/cancel?seller_id=${sellerId}`, {});
}

export function editPurchaseCost(purchaseId: number, unitCost: number, _sellerId: number) {
  return api.put(`/amazon/purchases/${purchaseId}/cost`, {
    unit_cost: unitCost,
  });
}

export function changeAsin(purchaseId: number, asin: string, _sellerId: number) {
  // Backend is PUT, not POST
  return api.put(`/amazon/purchases/${purchaseId}/change-asin`, {
    asin,
  });
}

export function generateLabel(sellerId: number, shipmentIds: number[], force = false) {
  return api.post('/labels/generate', {
    seller_id: sellerId,
    shipment_ids: shipmentIds,
    force,
  });
}

export function linkPurchase(mlOrderId: number, amazonOrderId: string, sellerId: number) {
  return api.post('/amazon/link', {
    seller_id: sellerId,
    ml_order_id: mlOrderId,
    amazon_order_id: amazonOrderId,
  });
}

export function unlinkPurchase(purchaseId: number, sellerId: number) {
  return api.delete(`/amazon/link/${purchaseId}?seller_id=${sellerId}`);
}

export function mergeLabels(sellerId: number, shipmentIds: number[]) {
  return api.post('/labels/merge', { seller_id: sellerId, shipment_ids: shipmentIds, force: true });
}

export async function bulkApprove(purchaseIds: number[]) {
  // Use backend bulk endpoint with controlled concurrency (2 at a time + delay)
  const res = await api.post('/amazon/purchases/bulk-approve', { ids: purchaseIds });
  if (!res.success) throw new Error((res as any).error || 'Bulk approve failed');
  return (res as any).data as { ok: number; failed: number; total: number; results: Array<{ id: number; success: boolean; error?: string }> };
}

export function deletePurchase(purchaseId: number, sellerId: number) {
  return api.delete(`/purchases/amazon/${purchaseId}?seller_id=${sellerId}`);
}

export function createPO(sellerId: number, purchaseIds: number[]) {
  return api.post('/amazon/po', { seller_id: sellerId, purchase_ids: purchaseIds });
}

// Internal stock actions
export function releaseStockPurchase(purchaseId: number) {
  return api.post(`/internal-stock/purchases/${purchaseId}/release-to-amazon`, {});
}

export function editStockPurchaseCost(purchaseId: number, unitCost: number) {
  return api.put(`/internal-stock/purchases/${purchaseId}/cost`, { unit_cost: unitCost });
}

// ── Seguir precio/stock en Keepa (2026-09-20) ──
export interface PriceWatchQuote {
  captured_price_usd: number | null;
  max_profit_price_usd: number | null;
  cached_amazon_price_usd: number | null;
}

export async function fetchPriceWatchQuote(purchaseId: number): Promise<PriceWatchQuote | null> {
  const res = await api.get<PriceWatchQuote>(`/amazon/purchases/${purchaseId}/price-watch/quote`);
  return res?.success && res.data ? res.data : null;
}

export function startPriceWatch(purchaseId: number, targetPriceUsd: number) {
  return api.post(`/amazon/purchases/${purchaseId}/price-watch`, { target_price_usd: targetPriceUsd });
}

export function stopPriceWatch(purchaseId: number) {
  return api.delete(`/amazon/purchases/${purchaseId}/price-watch`);
}
